import { useState } from "react";
import {
  initialBookingForm,
  validateBookingStep,
  submitBooking,
} from "@/lib/booking-api";
import { useCalcPrefill } from "@/hooks/use-calc-prefill";
import { trackBookingConversion } from "@/lib/analytics";

/**
 * Encapsulates the multi-step booking form state, validation, and submission.
 * Splitting further would only add ceremony; helpers live in `@/lib/booking-api`.
 */
export function useBookingForm(t, lang, prefill) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialBookingForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [serverError, setServerError] = useState("");

  useCalcPrefill(prefill, t, setForm);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validateCurrent = () => {
    const result = validateBookingStep(step, form, t);
    setErrors(result);
    return Object.keys(result).length === 0;
  };

  const next = () => {
    if (validateCurrent()) setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateCurrent()) return;
    setSubmitting(true);
    setServerError("");
    try {
      const data = await submitBooking(form, lang);
      setSuccess(data);
      trackBookingConversion();
    } catch {
      setServerError(t.booking.errors.server);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(null);
    setStep(1);
    setErrors({});
    setServerError("");
    setForm(initialBookingForm());
  };

  return {
    step, form, errors, submitting, success, serverError,
    set, next, back, submit, reset,
  };
}

export { todayPlusOne } from "@/lib/booking-api";
